import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Empty,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  BarChartOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import employeeService, {
  employeeKeys,
  type EmployeeListParams,
  type EmployeeRecord,
  type EmployeeShiftOverview,
  type EmployeeWorkWeekOverview,
  type ShiftAssignmentResult,
  type WorkWeekAssignmentResult,
} from '@/services/employee.service';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import departmentService from '@/services/department.service';
import { useAuthStore } from '@/store/authStore';
import { useWorkWeeks } from './shared/useWorkWeeks';

const { Text } = Typography;

type AssignMode = 'shift' | 'weekly_off';

const NO_SHIFT = '__none__';
const DEFAULT_SHIFT = '__default__';
const DEFAULT_WEEK = '__default_week__';
const EXITED = new Set(['Resigned', 'Terminated']);

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

type ShiftOption = { id: string; shift_code: string; shift_name: string; start_time?: string; end_time?: string; is_active?: boolean };

const shiftLabel = (s: ShiftOption) =>
  `${s.shift_code} — ${s.shift_name}${s.start_time ? ` (${s.start_time.slice(0, 5)}–${(s.end_time || '').slice(0, 5)})` : ''}`;

const EmployeeShiftAssignmentPage: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const canEdit =
    Boolean(user?.is_super_admin) ||
    Boolean(user?.permissions?.includes('hr.shift.manage')) ||
    Boolean(user?.permissions?.includes('hr.employee.edit'));

  const [mode, setMode] = useState<AssignMode>(searchParams.get('assign') === 'weekly-off' ? 'weekly_off' : 'shift');
  const [weekTarget, setWeekTarget] = useState<string>();
  const [weekFilter, setWeekFilter] = useState<string>();
  const [weekResult, setWeekResult] = useState<WorkWeekAssignmentResult | null>(null);
  const [shiftFilter, setShiftFilter] = useState<string | undefined>(searchParams.get('shift') || undefined);
  const [locationFilter, setLocationFilter] = useState<string>();
  const [departmentFilter, setDepartmentFilter] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [target, setTarget] = useState<string>();
  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs>(dayjs());
  const [effectiveTo, setEffectiveTo] = useState<Dayjs | null>(null);
  const [setDefault, setSetDefault] = useState(true);
  const [result, setResult] = useState<ShiftAssignmentResult | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const listParams: EmployeeListParams = useMemo(
    () => ({
      location_id: locationFilter,
      department_id: departmentFilter,
      employment_status: statusFilter,
      search: search.trim() || undefined,
    }),
    [locationFilter, departmentFilter, statusFilter, search]
  );

  const { data: employees = [], isLoading } = useQuery({
    queryKey: employeeKeys.list(listParams),
    queryFn: () => employeeService.list(listParams),
  });
  const { data: overview = [], isLoading: overviewLoading } = useQuery({
    queryKey: employeeKeys.shiftOverview(),
    queryFn: () => employeeService.shiftOverview(),
  });
  const { data: weekOverview = [], isLoading: weekOverviewLoading } = useQuery({
    queryKey: employeeKeys.workWeekOverview(),
    queryFn: () => employeeService.workWeekOverview(),
  });
  const { options: workWeekOptions, labelByCode: workWeekLabels } = useWorkWeeks();
  const { data: shifts = [] } = useQuery<ShiftOption[]>({
    queryKey: hrmsKeys.shifts(),
    queryFn: () => hrmsService.listShifts(),
  });
  const { data: locations = [] } = useQuery({
    queryKey: hrmsKeys.locations(),
    queryFn: () => hrmsService.listLocations(),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'selection', 'employees-list'],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 5 * 60 * 1000,
  });

  const overviewById = useMemo(
    () => new Map<string, EmployeeShiftOverview>(overview.map((o) => [o.employee_id, o])),
    [overview]
  );
  const weekOverviewById = useMemo(
    () => new Map<string, EmployeeWorkWeekOverview>(weekOverview.map((o) => [o.employee_id, o])),
    [weekOverview]
  );
  const shiftById = useMemo(() => new Map(shifts.map((s) => [s.id, s])), [shifts]);
  const deptNameById = useMemo(() => new Map(departments.map((d) => [d.id, d.department_name])), [departments]);
  const locationCodeById = useMemo(
    () => new Map(locations.map((l: { id: string; location_code: string }) => [l.id, l.location_code])),
    [locations]
  );

  const activeEmployees = useMemo(
    () => employees.filter((e) => !EXITED.has(String(e.employment_status))),
    [employees]
  );
  const rows = useMemo(
    () =>
      activeEmployees.filter((e) => {
        if (weekFilter && weekOverviewById.get(e.id)?.current_work_week !== weekFilter) return false;
        if (!shiftFilter) return true;
        const current = overviewById.get(e.id)?.current_shift_id || null;
        return shiftFilter === NO_SHIFT ? !current : current === shiftFilter;
      }),
    [activeEmployees, overviewById, shiftFilter, weekOverviewById, weekFilter]
  );
  const noShiftCount = activeEmployees.filter((e) => !overviewById.get(e.id)?.current_shift_id).length;

  const shiftName = (id: string | null | undefined) => {
    if (!id) return '—';
    const s = shiftById.get(id);
    return s ? s.shift_code : 'Unknown shift';
  };

  const temporary = Boolean(effectiveTo);
  const canSetDefault = Boolean(target) && target !== DEFAULT_SHIFT && !temporary;

  const targetOptions = [
    ...shifts.filter((s) => s.is_active !== false).map((s) => ({ value: s.id, label: shiftLabel(s) })),
    { value: DEFAULT_SHIFT, label: "Default shift (remove dated shifts in this period)" },
  ];
  const shiftFilterOptions = [
    { value: NO_SHIFT, label: 'No shift assigned' },
    ...shifts.map((s) => ({ value: s.id, label: `${shiftLabel(s)}${s.is_active === false ? ' (inactive)' : ''}` })),
  ];

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: employeeKeys.all });
  };

  const assignMutation = useMutation({
    mutationFn: () =>
      employeeService.assignShift({
        employee_ids: selectedIds.map(String),
        shift_id: target === DEFAULT_SHIFT ? null : target!,
        effective_from: effectiveFrom.format('YYYY-MM-DD'),
        effective_to: effectiveTo ? effectiveTo.format('YYYY-MM-DD') : null,
        set_default: canSetDefault && setDefault,
      }),
    onSuccess: (r) => {
      setResult(r);
      setSelectedIds([]);
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Assignment failed'),
  });

  const weekAssignMutation = useMutation({
    mutationFn: () =>
      employeeService.assignWorkWeek({
        employee_ids: selectedIds.map(String),
        work_week: weekTarget === DEFAULT_WEEK ? null : weekTarget!,
        effective_from: effectiveFrom.format('YYYY-MM-DD'),
        effective_to: effectiveTo ? effectiveTo.format('YYYY-MM-DD') : null,
      }),
    onSuccess: (r) => {
      setWeekResult(r);
      setSelectedIds([]);
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Assignment failed'),
  });

  const importMutation = useMutation({
    mutationFn: async (file: File) =>
      mode === 'weekly_off'
        ? { kind: 'week' as const, r: await employeeService.importWorkWeekAssignments(file) }
        : { kind: 'shift' as const, r: await employeeService.importShiftAssignments(file) },
    onSuccess: (out) => {
      if (out.kind === 'week') setWeekResult(out.r);
      else setResult(out.r);
      refresh();
    },
    onError: (e: any) => {
      const errors: string[] | undefined = e?.response?.data?.data?.errors;
      if (errors?.length) setImportErrors(errors);
      else message.error(e?.response?.data?.message || e?.message || 'Import failed');
    },
  });

  const confirmAssign = () => {
    const targetLabel =
      target === DEFAULT_SHIFT ? 'their default shift' : shiftById.get(target!) ? shiftLabel(shiftById.get(target!)!) : 'the shift';
    const period = effectiveTo
      ? `from ${effectiveFrom.format('DD MMM YYYY')} to ${effectiveTo.format('DD MMM YYYY')}. The previous shift resumes the next day.`
      : `from ${effectiveFrom.format('DD MMM YYYY')} onwards.`;
    modal.confirm({
      title: `Assign ${selectedIds.length} employee(s) to ${targetLabel}?`,
      content: (
        <Space direction="vertical" size={4}>
          <Text>Effective {period}</Text>
          <Text type="secondary">
            Overlapping shift assignments are trimmed automatically. Employees with a submitted or approved timesheet in this
            period are skipped.
          </Text>
          {canSetDefault && setDefault && <Text type="secondary">The shift also becomes each employee's default shift.</Text>}
        </Space>
      ),
      okText: 'Assign',
      onOk: () => assignMutation.mutateAsync(),
    });
  };

  const confirmWeekAssign = () => {
    const targetLabel =
      weekTarget === DEFAULT_WEEK
        ? 'their location / company weekly off'
        : `${workWeekLabels.get(weekTarget!) || weekTarget} weekly off`;
    const period = effectiveTo
      ? `from ${effectiveFrom.format('DD MMM YYYY')} to ${effectiveTo.format('DD MMM YYYY')}. The previous weekly off resumes the next day.`
      : `from ${effectiveFrom.format('DD MMM YYYY')} onwards.`;
    modal.confirm({
      title: `Assign ${selectedIds.length} employee(s) to ${targetLabel}?`,
      content: (
        <Space direction="vertical" size={4}>
          <Text>Effective {period}</Text>
          <Text type="secondary">
            Overlapping weekly off assignments are trimmed automatically. Employees with a submitted or approved timesheet in
            this period are skipped.
          </Text>
        </Space>
      ),
      okText: 'Assign',
      onOk: () => weekAssignMutation.mutateAsync(),
    });
  };

  const handleTemplate = async () => {
    try {
      if (mode === 'weekly_off') {
        saveBlob(
          await employeeService.downloadWorkWeekAssignmentTemplateBlob(),
          'employee-weekly-off-assignment-template.xlsx'
        );
      } else {
        saveBlob(await employeeService.downloadShiftAssignmentTemplateBlob(), 'employee-shift-assignment-template.xlsx');
      }
    } catch {
      message.error('Template download failed');
    }
  };

  const handleExport = async () => {
    try {
      if (mode === 'weekly_off') {
        saveBlob(
          await employeeService.exportWorkWeekAssignmentsBlob(listParams),
          `employee-weekly-off-${dayjs().format('YYYY-MM-DD')}.xlsx`
        );
      } else {
        saveBlob(
          await employeeService.exportShiftAssignmentsBlob(listParams),
          `employee-shifts-${dayjs().format('YYYY-MM-DD')}.xlsx`
        );
      }
    } catch {
      message.error('Export failed');
    }
  };

  const weekLabel = (code: string | null | undefined) => (code ? workWeekLabels.get(code) || code : '—');
  const weekTargetOptions = [
    ...workWeekOptions,
    { value: DEFAULT_WEEK, label: 'Location / company weekly off (remove dated weekly off in this period)' },
  ];

  const columns: ColumnsType<EmployeeRecord> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{`${r.first_name} ${r.last_name}`}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.employee_code}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Department',
      key: 'department',
      width: 180,
      render: (_, r) => (r.department_id ? deptNameById.get(r.department_id) || '—' : '—'),
    },
    {
      title: 'Location',
      key: 'location',
      width: 120,
      render: (_, r) => (r.location_id ? locationCodeById.get(r.location_id) || '—' : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'employment_status',
      width: 110,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Current shift',
      key: 'shift',
      width: 260,
      render: (_, r) => {
        const o = overviewById.get(r.id);
        if (!o?.current_shift_id) return <Tag color="orange">No shift</Tag>;
        const s = shiftById.get(o.current_shift_id);
        return (
          <Space size={4} wrap>
            <ClockCircleOutlined />
            <Text>{s ? shiftLabel(s) : 'Unknown shift'}</Text>
            {s?.is_active === false && <Tag color="red">Inactive</Tag>}
            {o.current_source === 'assignment' ? (
              <Tag color="blue">{o.current_until ? `until ${dayjs(o.current_until).format('DD MMM YYYY')}` : 'Assigned'}</Tag>
            ) : (
              <Tag>Default</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Next change',
      key: 'next',
      width: 190,
      render: (_, r) => {
        const o = overviewById.get(r.id);
        if (!o?.next_shift_id || !o.next_from) return '—';
        return (
          <Text>
            {shiftName(o.next_shift_id)} from {dayjs(o.next_from).format('DD MMM YYYY')}
          </Text>
        );
      },
    },
    {
      title: 'Weekly off',
      key: 'weekly_off',
      width: 280,
      render: (_, r) => {
        const o = weekOverviewById.get(r.id);
        if (!o) return '—';
        return (
          <Space direction="vertical" size={0}>
            <Space size={4} wrap>
              <CalendarOutlined />
              <Text>{weekLabel(o.current_work_week)}</Text>
              {o.current_source === 'assignment' ? (
                <Tag color="blue">{o.current_until ? `until ${dayjs(o.current_until).format('DD MMM YYYY')}` : 'Assigned'}</Tag>
              ) : (
                <Tag>{o.current_source === 'location' ? 'Location' : 'Company'}</Tag>
              )}
            </Space>
            {o.next_work_week && o.next_from && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {o.next_work_week} from {dayjs(o.next_from).format('DD MMM YYYY')}
              </Text>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Employee shift & weekly off assignment"
          subtitle="Bulk assign or reassign shifts and weekly off days, permanently or for a period (rotation, Ramadan, night cover)"
          primaryActions={
            <Space wrap>
              <Button icon={<BarChartOutlined />} onClick={() => navigate('/hr/reports/shift-roster')}>
                Shift roster
              </Button>
              <Button icon={<FileExcelOutlined />} onClick={handleTemplate}>
                {mode === 'weekly_off' ? 'Weekly off template' : 'Shift template'}
              </Button>
              {canEdit && (
                <Button icon={<UploadOutlined />} loading={importMutation.isPending} onClick={() => fileInputRef.current?.click()}>
                  {mode === 'weekly_off' ? 'Import weekly off' : 'Import shifts'}
                </Button>
              )}
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                {mode === 'weekly_off' ? 'Export weekly off' : 'Export shifts'}
              </Button>
            </Space>
          }
        />
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) importMutation.mutate(file);
        }}
      />

      {canEdit && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Segmented<AssignMode>
            style={{ marginBottom: 12 }}
            value={mode}
            onChange={setMode}
            options={[
              { value: 'shift', label: 'Shift', icon: <ClockCircleOutlined /> },
              { value: 'weekly_off', label: 'Weekly off', icon: <CalendarOutlined /> },
            ]}
          />
          <Space wrap align="center" style={{ display: 'flex' }}>
            <Text strong>{selectedIds.length ? `${selectedIds.length} employee(s) selected` : 'Select employees below, then'}</Text>
            {mode === 'shift' ? (
              <Select
                placeholder="Shift"
                style={{ width: 320 }}
                showSearch
                optionFilterProp="label"
                options={targetOptions}
                value={target}
                onChange={setTarget}
              />
            ) : (
              <Select
                placeholder="Weekly off (work week)"
                style={{ width: 380 }}
                showSearch
                optionFilterProp="label"
                options={weekTargetOptions}
                value={weekTarget}
                onChange={setWeekTarget}
              />
            )}
            <DatePicker
              value={effectiveFrom}
              onChange={(d) => {
                const next = d || dayjs();
                setEffectiveFrom(next);
                if (effectiveTo && effectiveTo.isBefore(next, 'day')) setEffectiveTo(null);
              }}
              allowClear={false}
              format="DD MMM YYYY"
              placeholder="From"
            />
            <Tooltip
              title={`Leave blank for a permanent change. With an end date the previous ${
                mode === 'shift' ? 'shift' : 'weekly off'
              } resumes the next day.`}
            >
              <DatePicker
                value={effectiveTo}
                onChange={setEffectiveTo}
                format="DD MMM YYYY"
                placeholder="To (optional)"
                disabledDate={(d) => d.isBefore(effectiveFrom, 'day')}
              />
            </Tooltip>
            {mode === 'shift' ? (
              <>
                <Checkbox
                  checked={canSetDefault && setDefault}
                  disabled={!canSetDefault}
                  onChange={(e) => setSetDefault(e.target.checked)}
                >
                  Also set as default shift
                </Checkbox>
                <Button
                  type="primary"
                  disabled={!selectedIds.length || !target}
                  loading={assignMutation.isPending}
                  onClick={confirmAssign}
                >
                  Assign shift
                </Button>
              </>
            ) : (
              <Button
                type="primary"
                disabled={!selectedIds.length || !weekTarget}
                loading={weekAssignMutation.isPending}
                onClick={confirmWeekAssign}
              >
                Assign weekly off
              </Button>
            )}
          </Space>
          {mode === 'weekly_off' && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              Without an assignment, the employee follows the location work week, else the company standard (HRMS Setup).
              Add patterns such as Mon – Sat in HRMS Setup → Work weeks.
            </Text>
          )}
        </Card>
      )}

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search name, code, email"
            allowClear
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="Current shift"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 280 }}
            options={shiftFilterOptions}
            value={shiftFilter}
            onChange={(v) => {
              setShiftFilter(v);
              setSelectedIds([]);
            }}
          />
          <Select
            placeholder="Current weekly off"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
            options={workWeekOptions}
            value={weekFilter}
            onChange={(v) => {
              setWeekFilter(v);
              setSelectedIds([]);
            }}
          />
          <Select
            placeholder="Location"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            options={locations.map((l: { id: string; location_code: string; location_name: string }) => ({
              value: l.id,
              label: `${l.location_code} — ${l.location_name}`,
            }))}
            value={locationFilter}
            onChange={setLocationFilter}
          />
          <Select
            placeholder="Department"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
            value={departmentFilter}
            onChange={setDepartmentFilter}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 150 }}
            options={['Active', 'Onboarding', 'On Leave', 'Suspended', 'Inactive'].map((s) => ({ value: s, label: s }))}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          {noShiftCount > 0 && !shiftFilter && (
            <Button type="link" onClick={() => setShiftFilter(NO_SHIFT)}>
              {noShiftCount} without a shift
            </Button>
          )}
        </Space>
        <Table<EmployeeRecord>
          rowKey="id"
          loading={isLoading || overviewLoading || weekOverviewLoading}
          columns={columns}
          dataSource={rows}
          rowSelection={
            canEdit ? { selectedRowKeys: selectedIds, onChange: setSelectedIds, preserveSelectedRowKeys: true } : undefined
          }
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No employees" /> }}
        />
      </Card>

      <Modal
        title="Shift assignment done"
        open={Boolean(result)}
        onCancel={() => setResult(null)}
        footer={<Button onClick={() => setResult(null)}>Close</Button>}
      >
        {result && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              {result.changed} employee(s) changed, {result.unchanged} already on that shift
              {result.defaults_updated ? `, ${result.defaults_updated} default shift(s) updated` : ''}.
            </Text>
            {result.skipped.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${result.skipped.length} skipped`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {result.skipped.map((s) => (
                      <li key={s.employee_code}>
                        {s.employee_code}: {s.reason}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            {result.regenerate_months.length > 0 && (
              <Alert
                type="info"
                showIcon
                message="Regenerate timesheets"
                description={
                  <Space direction="vertical" size={2}>
                    <Text>These auto-generated timesheet months still use the old shift:</Text>
                    {result.regenerate_months.map((m) => (
                      <Typography.Link key={m.period} onClick={() => navigate(`/hr/attendance/generate?month=${m.period}`)}>
                        {dayjs(`${m.period}-01`).format('MMMM YYYY')} — {m.employees} employee(s)
                      </Typography.Link>
                    ))}
                  </Space>
                }
              />
            )}
          </Space>
        )}
      </Modal>

      <Modal
        title="Weekly off assignment done"
        open={Boolean(weekResult)}
        onCancel={() => setWeekResult(null)}
        footer={<Button onClick={() => setWeekResult(null)}>Close</Button>}
      >
        {weekResult && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              {weekResult.changed} employee(s) changed, {weekResult.unchanged} already on that weekly off.
            </Text>
            {weekResult.skipped.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${weekResult.skipped.length} skipped`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {weekResult.skipped.map((s) => (
                      <li key={s.employee_code}>
                        {s.employee_code}: {s.reason}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            {weekResult.regenerate_months.length > 0 && (
              <Alert
                type="info"
                showIcon
                message="Regenerate timesheets"
                description={
                  <Space direction="vertical" size={2}>
                    <Text>These auto-generated timesheet months still use the old weekly off:</Text>
                    {weekResult.regenerate_months.map((m) => (
                      <Typography.Link key={m.period} onClick={() => navigate(`/hr/attendance/generate?month=${m.period}`)}>
                        {dayjs(`${m.period}-01`).format('MMMM YYYY')} — {m.employees} employee(s)
                      </Typography.Link>
                    ))}
                  </Space>
                }
              />
            )}
          </Space>
        )}
      </Modal>

      <Modal
        title="Import failed — nothing was imported"
        open={importErrors.length > 0}
        onCancel={() => setImportErrors([])}
        footer={<Button onClick={() => setImportErrors([])}>Close</Button>}
      >
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Fix these rows in the file and upload it again." />
        <ul style={{ maxHeight: 320, overflow: 'auto', paddingLeft: 18 }}>
          {importErrors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </Modal>
    </WorkspaceLayout>
  );
};

export default EmployeeShiftAssignmentPage;
